import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { DataTransferCost } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOAD_DATA_TRANSFER_COST_QUERY_KEY = 'workload-data-transfer-cost';

type QueryParameters = {
  clusterId: string;
  namespace: string;
  workload: string;
  workloadType: string;
  startTime: string;
  endTime: string;
  stepSeconds?: number;
  enabled?: boolean;
  refetchInterval?: (data: unknown) => number | false;
};

const getWorkloadDataTransferCostQueryKey = ({
  clusterId,
  namespace,
  workload,
  workloadType,
  startTime,
  endTime,
  stepSeconds,
}: Pick<
  QueryParameters,
  | 'clusterId'
  | 'namespace'
  | 'workload'
  | 'workloadType'
  | 'startTime'
  | 'endTime'
  | 'stepSeconds'
>) =>
  [
    WORKLOAD_DATA_TRANSFER_COST_QUERY_KEY,
    clusterId,
    namespace,
    workload,
    workloadType,
    startTime,
    endTime,
    stepSeconds,
  ] as const;

const getWorkloadDataTransferCostQueryOptions = (
  {
    clusterId,
    namespace,
    workload,
    workloadType,
    startTime,
    endTime,
    stepSeconds,
    refetchInterval,
    enabled = true,
  }: QueryParameters,
  client: ReturnType<typeof useQueryClient>
) =>
  queryOptions<{ item: DataTransferCost }>({
    queryKey: getWorkloadDataTransferCostQueryKey({
      clusterId,
      namespace,
      workload,
      workloadType,
      startTime,
      endTime,
      stepSeconds,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getWorkloadDataTransferCost({
        clusterId,
        namespace,
        workloadType,
        workloadName: workload,
        startTime,
        endTime,
        stepSeconds,
      });

      return {
        item: data.item!,
      };
    },
    enabled: enabled && !!clusterId,
    refetchInterval: ({ queryKey }) => {
      if (refetchInterval) {
        return refetchInterval(client.getQueryData(queryKey));
      }
      return false;
    },
  });

export const useWorkloadDataTransferCostQuery = (
  parameters: QueryParameters
) => {
  const client = useQueryClient();
  return useQuery(getWorkloadDataTransferCostQueryOptions(parameters, client));
};
